import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Move, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import {
  DEFAULT_PHOTO_FOCAL_POINT,
  normalizePhotoFocalPoint,
  panFocalPoint,
  photoObjectPosition,
  type PhotoFocalPoint,
} from '../lib/photoFocalPoint';

interface StorefrontPhotoPositionEditorProps {
  storeId: number;
  storeIdParam: string;
  imageUrl: string;
  focalPoint: PhotoFocalPoint;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StorefrontPhotoPositionEditor({
  storeId,
  storeIdParam,
  imageUrl,
  focalPoint,
  open,
  onOpenChange,
}: StorefrontPhotoPositionEditorProps) {
  const [draftFocal, setDraftFocal] = useState<PhotoFocalPoint>(focalPoint);
  const [isSaving, setIsSaving] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; x: number; y: number; focal: PhotoFocalPoint } | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setDraftFocal(normalizePhotoFocalPoint(focalPoint.x, focalPoint.y));
    }
  }, [open, focalPoint.x, focalPoint.y]);

  const invalidateStoreQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/stores', 'store-profiles'] }),
    ]);
  };

  const beginDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      focal: draftFocal,
    };
  };

  const dragImage = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const frame = frameRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !frame) return;

    const deltaX = event.clientX - drag.x;
    const deltaY = event.clientY - drag.y;
    const bounds = frame.getBoundingClientRect();
    const nextFocal = panFocalPoint(drag.focal, deltaX, deltaY, bounds.width, bounds.height);
    setDraftFocal(nextFocal);
    dragRef.current = {
      pointerId: drag.pointerId,
      x: event.clientX,
      y: event.clientY,
      focal: nextFocal,
    };
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  const resetFraming = () => {
    setDraftFocal(DEFAULT_PHOTO_FOCAL_POINT);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiRequest(`/api/store-profiles/${storeId}/storefront-photo/framing`, 'PATCH', {
        photoFocalX: draftFocal.x,
        photoFocalY: draftFocal.y,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save photo framing');
      }
      await invalidateStoreQueries();
      toast({ title: 'Photo framing saved' });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Could not save photo framing',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Adjust storefront framing</DialogTitle>
          <DialogDescription>
            Drag the photo to choose what appears in the header banner. The image is not cropped — only the visible area changes.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={frameRef}
          className="relative w-full h-48 md:h-64 rounded-xl overflow-hidden border bg-gray-100 cursor-grab active:cursor-grabbing touch-none"
          onPointerDown={beginDrag}
          onPointerMove={dragImage}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <img
            src={imageUrl}
            alt="Storefront framing preview"
            className="w-full h-full object-cover select-none pointer-events-none"
            style={{ objectPosition: photoObjectPosition(draftFocal) }}
            draggable={false}
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-black/10 pointer-events-none" />
          <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs text-white pointer-events-none">
            <Move className="h-3 w-3" />
            Drag to reposition
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Drag the photo until the storefront looks right in the banner preview above.
        </p>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={resetFraming} disabled={isSaving}>
            Reset to center
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Save framing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
